import { useEffect, useMemo, useState } from 'react';
import './kpi.css';
import { useApiData } from '../../contexts/apiService';
import { MoonLoader } from 'react-spinners';
import { useUserAccess } from '../../contexts/userAccess';
import Dropdown, { DropdownOption } from '../../components/common/dropdown/dropdown';
import ProducerBarChart from '../../components/charts/ProducerBSWChart';
import ConfigurableBarChart from '../../components/charts/ConfigurableBarChart';
import GenericBarChart from '../../components/charts/GenericBarChart';

/**
 * Interface for producer selection options
 * @interface ProducerOption
 * @property {string} label - Display name for the producer
 * @property {string} value - Unique identifier for the producer
 */
interface ProducerOption {
  label: string;
  value: string;
}


/**
 * Utility function to round numbers to specified decimal places
 * Uses scientific notation approach to avoid floating point precision issues
 * 
 * @param value - The number to round
 * @param decimals - Number of decimal places to round to
 * @returns Rounded number with specified decimal precision
 */
function round(value: number, decimals: number): number {
  return Number(Math.round(Number(value + 'e' + decimals)) + 'e-' + decimals);
}


/**
 * KPI Dashboard Main Component
 * 
 * Provides a comprehensive Key Performance Indicator dashboard with:
 * - Tabbed navigation (Load Statistics, Producer Efficiency, Gain/Loss, Custom Graphs)
 * - Month/year filtering with automatic data fetching
 * - Multi-producer selection and filtering
 * - Interactive charts with statistical analysis
 * - Admin access control
 * 
 * The component manages data flow between user selections and chart components,
 * handles empty data scenarios with automatic API requests, and provides
 * a consistent interface for various KPI analysis tools.
 */
const KpiPage = () => {
  // API data context and user permissions
  const { apiData, isLoading, handleMonthRequest } = useApiData();
  const { hasAdminAccess, isLoading: isFetchingUserAccess } = useUserAccess();

  // Month/Year filtering state management
  const [selectedMonthYear, setSelectedMonthYear] = useState<string>('');          // Currently selected month/year (format: "YYYY-MM")
  const [alreadyFetched, setAlreadyFetched] = useState<Set<string>>(new Set());    // Track attempted API requests to prevent duplicates

  // Producer filtering state
  const [producerSelection, setProducerSelection] = useState<string[]>([]);        // Array of selected producer names for filtering

  /**
   * Memoized generation of month/year dropdown options
   * 
   * Creates dropdown options from the available date range in the API data.
   * Generates all month/year combinations between min and max dates,
   * formatted for user display and component value handling.
   * 
   * Returns options in reverse chronological order (most recent first).
   * Handles edge cases: missing date ranges, invalid dates, empty data.
   */
  const monthYearOptions = useMemo((): DropdownOption[] => {
    if (!apiData?.date_ranges?.ticket_range) return [];
    
    const { min_date, max_date } = apiData.date_ranges.ticket_range;
    
    // Validate date range exists
    if (!min_date || !max_date) return [];
    
    const startDate = new Date(min_date);
    const endDate = new Date(max_date);
    
    // Validate dates are parseable
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return [];
    
    const options: DropdownOption[] = [];
    const current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    
    // Generate option for each month in the range
    while (current <= endDate) {
      const year = current.getFullYear();
      const month = current.getMonth();
      const monthName = current.toLocaleString('default', { month: 'long' });
      
      const value = `${year}-${String(month + 1).padStart(2, '0')}`; // Format: "2024-07"
      const label = `${monthName} ${year}`;                          // Format: "July 2024"
      
      options.push({ value, label });
      
      // Advance to next month
      current.setMonth(current.getMonth() + 1);
    }
    
    return options.reverse(); // Most recent first for user convenience
  }, [apiData?.date_ranges?.ticket_range]);

  /**
   * Effect to set intelligent default month/year selection
   * 
   * Automatically selects the current month if available in the dataset,
   * otherwise defaults to the most recent available month.
   * Only runs when month options are first loaded to avoid overriding user selections.
   */
  useEffect(() => {
    if (monthYearOptions.length > 0 && !selectedMonthYear) {
      // Try to find and select the current month
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1;
      const currentMonthYear = `${currentYear}-${String(currentMonth).padStart(2, '0')}`;
      
      // Use current month if available, otherwise fall back to latest available
      const currentOption = monthYearOptions.find(opt => opt.value === currentMonthYear);
      setSelectedMonthYear(currentOption ? currentMonthYear : monthYearOptions[0].value);
    }
  }, [monthYearOptions, selectedMonthYear]);

  /**
   * Handler for month/year dropdown selection changes
   * Updates the selected month/year state to trigger data filtering
   */
  const handleMonthYearChange = (monthYear: string) => {
    setSelectedMonthYear(monthYear);
  };

  /**
   * Handler for empty month data scenarios
   * 
   * When a selected month has no data, attempts to fetch it from the API.
   * Uses a tracking set to prevent duplicate requests for the same month.
   * Converts month/year format to the API's expected format.
   * 
   * @param monthYear - Selected month in "YYYY-MM" format
   */
  const handleEmptyMonth = (monthYear: string) => {
    const [year, month] = monthYear.split('-');
    const monthDate = new Date(parseInt(year), parseInt(month) - 1, 1);
    const monthKey = `main_table-${monthDate.toLocaleString('en-US', { month: 'long', year: 'numeric' })}`;
    
    // Prevent duplicate API requests for the same month
    if (!alreadyFetched.has(monthKey)) {
      setAlreadyFetched(prev => new Set(Array.from(prev).concat(monthKey)));
      handleMonthRequest('main_table', monthDate.toLocaleString('en-US', { month: 'long', year: 'numeric' }));
    }
  };

  /**
   * Memoized filtering of data by selected month/year
   * 
   * Filters the main dataset to only include records matching the selected month/year.
   * Converts the "YYYY-MM" format to match the API data structure which uses
   * separate year (string) and month (full name) fields.
   * 
   * Returns empty array if no data is available or no month is selected.
   */
  const monthYearFilteredData = useMemo(() => {
    if (!apiData?.main_table || !selectedMonthYear) return [];
    
    const [year, month] = selectedMonthYear.split('-');
    const selectedYear = parseInt(year);
    const selectedMonthName = new Date(selectedYear, parseInt(month) - 1, 1)
      .toLocaleString('default', { month: 'long' });

    return apiData.main_table.filter(item => {
      return item.year === String(selectedYear) && item.month === selectedMonthName;
    });
  }, [apiData?.main_table, selectedMonthYear]);

  /**
   * Effect to handle empty month data scenarios
   * 
   * Monitors the filtered data length and automatically triggers
   * API requests for months with no data. This provides seamless
   * user experience by fetching missing data in the background.
   */
  useEffect(() => {
    if (monthYearFilteredData && monthYearFilteredData.length === 0 && selectedMonthYear) {
      const [year, month] = selectedMonthYear.split('-');
      const monthDate = new Date(parseInt(year), parseInt(month) - 1, 1);
      const monthKey = `main_table-${monthDate.toLocaleString('en-US', { month: 'long', year: 'numeric' })}`;
      
      // Only fetch if we haven't already tried this month
      if (!alreadyFetched.has(monthKey)) {
        handleEmptyMonth(selectedMonthYear);
      }
    }
  }, [monthYearFilteredData?.length, selectedMonthYear, alreadyFetched]);

  /**
   * Memoized extraction of unique producer names from filtered data
   * 
   * Creates a list of available producers for the selected month/year.
   * Filters out null/undefined producer names and removes duplicates.
   * Recalculates only when the month/year filtered data changes.
   */
  const filteredProducerOptions = useMemo(() => {
    if (!monthYearFilteredData) return [];
    const names = monthYearFilteredData
      .map((item: any) => item.producer)
      .filter((name: string | undefined) => !!name);
    return Array.from(new Set(names));
  }, [monthYearFilteredData]);

  /**
   * Effect for intelligent producer auto-selection
   * 
   * Automatically selects all available producers when:
   * 1. Data loading is complete
   * 2. Producer options are available 
   * 3. No producers are currently selected
   * 
   * This provides a better initial user experience by showing all data by default.
   */
  useEffect(() => {
    if (!isLoading && filteredProducerOptions.length > 0 && producerSelection.length === 0) {
      setProducerSelection(filteredProducerOptions);
    }
  }, [isLoading, filteredProducerOptions, producerSelection.length]);

  /**
   * Handler for multi-select producer dropdown changes
   * 
   * Extracts selected values from the multi-select element and updates
   * the producer selection state. Supports multiple producer selection
   * for comparative analysis across different producers.
   */
  const handleProducerSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = Array.from(e.target.selectedOptions).map(opt => opt.value);
    setProducerSelection(selected);
  };

  /**
   * Final filtered dataset for chart components
   * 
   * Applies both month/year and producer filtering to the raw data.
   * This is the primary data source passed to all chart components.
   * 
   * Filter chain: Raw Data → Month/Year Filter → Producer Filter → Charts
   */
  const filteredData = useMemo(() => {
    if (!monthYearFilteredData || !Array.isArray(monthYearFilteredData)) {
      return [];
    }
    return monthYearFilteredData.filter(item => producerSelection.includes(item.producer));
  }, [monthYearFilteredData, producerSelection]);

  /**
   * Tab navigation configuration
   * Defines the available KPI analysis sections with their identifiers and display labels
   */
  const tabOptions = [
    { key: 'loadStats', label: 'Load Statistics' },           // BS&W analysis and statistics
    { key: 'producerEfficiency', label: 'Producer Efficiency' }, // Efficiency metrics (placeholder)
    { key: 'gainLoss', label: 'Gain/Loss' },                 // Gain/Loss analysis (placeholder)
    { key: 'customGraph', label: 'Customized Graph' },       // Configurable chart interface
  ];
  
  /** Current active tab state for conditional rendering */
  const [activeTab, setActiveTab] = useState<string>('loadStats');

  return (
    <div className='kpi_pageContainer'>
      {/* Fixed Header Area - Controls and Tab Navigation */}
      <div className='kpi_fixedHeader'>
        {/* Main Controls Row - Data Filtering Interface */}
        <div className='kpi_controlsRow'>
          {/* Month/Year Selection Component */}
          <div className='kpi_monthSelectContainer'>
            <label htmlFor='month-year-select' className='kpi_label'>Select Month/Year:</label>
            <Dropdown
              options={monthYearOptions}
              value={selectedMonthYear}
              onChange={handleMonthYearChange}
              placeholder="Select Month/Year"
            />
          </div>
          
          {/* Multi-Producer Selection Component */}
          <div className='kpi_producerSelectContainer'>
            <label htmlFor='producer-select' className='kpi_label' style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              Select Producers:
              <span className="producer-tooltip-container">
                <span
                  className="producer-tooltip-icon"
                  tabIndex={0}
                  aria-label="Producer selection info"
                >?
                </span>
                <span
                  className="producer-tooltip"
                >
                  Select one or more producers to filter the KPI charts. Hold Ctrl (Cmd on Mac) to select multiple producers for comparison. Ctrl + A to select all.
                </span>
              </span>
            </label>
            <select
              id='producer-select'
              multiple
              value={producerSelection}
              onChange={handleProducerSelect}
              className='kpi_producerSelect'
            >
              {filteredProducerOptions.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Tab Navigation Bar */}
        <div className='tab-selector'>
          {tabOptions.map(tab => (
            <button
              key={tab.key}
              className={`tab-selector-button${activeTab === tab.key ? ' active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      
      {/* Main Content Area with Conditional Rendering */}
      {
        isLoading || isFetchingUserAccess ? (
          /* Loading State - Show spinner while fetching data or checking permissions */
          <div className='kpi_loaderOverlay'>
            <MoonLoader size={35} color="#2973A6" />
          </div>
        ) : hasAdminAccess ? (
          /* Authorized Content - Render selected tab content */
          <div className='kpi_tabContent'>
            {/* Load Statistics Tab - BS&W analysis charts */}
            {activeTab === 'loadStats' && (
              <>
                <ProducerBarChart filteredData={filteredData} producerSelection={producerSelection} />
                <GenericBarChart 
                  filteredData={filteredData}
                  producerSelection={producerSelection}
                  selected_variable="responseTime"  // or "api_gravity", "net_barrels", etc.
                />
                <GenericBarChart 
                  filteredData={filteredData}
                  producerSelection={producerSelection}
                  selected_variable="corrected_gravity"  // or "api_gravity", "net_barrels", etc.
                />
              </>
            )}
            
            {/* Producer Efficiency Tab - Placeholder for future implementation */}
            {activeTab === 'producerEfficiency' && (
              <>
                <GenericBarChart 
                  filteredData={filteredData}
                  producerSelection={producerSelection}
                  selected_variable="mileage"  // or "api_gravity", "net_barrels", etc.
                />
              </>
            )}
            
            {/* Gain/Loss Tab - Placeholder for future implementation */}
            {activeTab === 'gainLoss' && (
              <div>Gain/Loss content goes here.</div>
            )}
            
            {/* Custom Graph Tab - Interactive configurable charts */}
            {activeTab === 'customGraph' && (
              <ConfigurableBarChart filteredData={filteredData} />
            )}
          </div>
        ) : (
          /* Access Denied - Show error message for unauthorized users */
          <div className='kpi_accessDenied'>
            You do not have access to view this page. Please contact your administrator if you believe this is an error.
          </div>
        )
      }
    </div>
  )
}

export default KpiPage;

